import { productUtilities } from '@simmons/product-platform-config';
import {
  computed,
  ref,
  unref,
  watchEffect,
  type ComputedRef,
  type Ref,
} from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import type { ActiveProduct } from '@/config';
import { ProductLabels } from '@/config/constants';
import {
  excludeNullish,
  getPermissionProducts,
  isActiveProduct,
  removePortalSuffix,
  toPortalProduct,
} from '@/utils';
import getAllRoutes from '@/utils/getAllRoutes';

import { useStore } from '@/store';
import { AuthGetters } from '@/store/modules/auth/auth.types';

const { resolveSpecialLocationPermissionMapping } = productUtilities;

type ProductReturn = ActiveProduct | null;

type ProductOption = {
  icon: Record<string, unknown> | null;
  label: string;
  value: ActiveProduct;
};

type UseUserAccessReturnType = {
  canAccessMoreThanOneProduct: Ref<boolean>;
  cannotAccessAnyProduct: Ref<boolean>;
  isDocumentOwner: ComputedRef<Promise<boolean>>;
  isOwner: Ref<boolean>;
  productsUserCanAccess: ProductOption[];
  routesUserCanAccess: RouteRecordRaw[];
  locationPermissionFormat: Ref<string>;
};

export function useUserCollabPermissions() {
  const { useGetter } = useStore();

  return useGetter<
    ReturnType<AuthGetters['userCollaborationPermissions']>
  >('auth/userCollaborationPermissions');
}

const getValidProductsUserCanAccess = (
  usersProducts: ActiveProduct[],
): ProductOption[] => usersProducts.map((product) => ({
  label: ProductLabels[product],
  value: product,
  icon: null,
}));

function getProductsUserCanAccess(userCollabPermissions: string[]): ProductOption[] {
  const userProducts: ActiveProduct[] = getPermissionProducts(userCollabPermissions)
    .map(toPortalProduct)
    .filter(excludeNullish)
    .filter(isActiveProduct);

  return getValidProductsUserCanAccess(userProducts);
}

function getRoutesUserCanAccess(userCollabPermissions: string[]): RouteRecordRaw[] {
  const allAvailableRoutes: RouteRecordRaw[] = getAllRoutes();

  return allAvailableRoutes.filter((routeRecord) => {
    if (routeRecord.meta?.requiredPermissions) {
      return (routeRecord.meta.requiredPermissions as string[]).every(
        (routePermission) => userCollabPermissions.some(
          (permissionString: string) => permissionString.includes(routePermission),
        ),
      );
    }

    return false;
  });
}

async function getLocationPermissionFormat(product: ActiveProduct, location: string):
Promise<string> {
  const locationFormat = location.toLowerCase();
  const mappedPermission = await resolveSpecialLocationPermissionMapping(product, locationFormat);
  return mappedPermission.replace(/[^\w\s]/gi, '').replace(/ /g, '-');
}

async function getIsDocumentOwner(
  product: ActiveProduct,
  location: string,
  userCollabPermissions: string[],
): Promise<boolean> {
  if (
    !location
    || !product
    || !userCollabPermissions.length
  ) return false;

  const platformProduct = product && removePortalSuffix(product);
  const permissionLocation = await getLocationPermissionFormat(product, location);
  const permission = `|${platformProduct}-collaboration-owner:${permissionLocation}`;
  const isOwnerD = userCollabPermissions.find((item) => item.endsWith(permission));

  return !!isOwnerD;
}

function getIsOwner(product: ProductReturn, userCollabPermissions: string[]): boolean {
  const platformProduct = product && removePortalSuffix(product);

  if (!platformProduct || !userCollabPermissions.length) return false;

  const hasOwnerPermission = userCollabPermissions.find(
    (item: string) => item.includes(platformProduct) && item.includes('owner'),
  );

  return !!hasOwnerPermission;
}

export default function useUserAccess(
  product: Ref<ProductReturn> | ComputedRef<ProductReturn> | ProductReturn,
  location?: Ref<string> | ComputedRef<string>,
): UseUserAccessReturnType | never {
  if (!product || unref(product) === null) {
    throw new Error('useUserAccess requires a valid active product');
  }

  const userCollabPermissions = useUserCollabPermissions();

  const productsUserCanAccess: ProductOption[] = getProductsUserCanAccess(userCollabPermissions);
  const routesUserCanAccess: RouteRecordRaw[] = getRoutesUserCanAccess(userCollabPermissions);
  const isDocumentOwner = computed(async () => {
    const locationValue = unref(location) ?? null;
    const productValue = unref(product) ?? null;

    if (!locationValue || !productValue) return false;

    // eslint-disable-next-line max-len
    return (await getIsDocumentOwner(productValue, locationValue, userCollabPermissions)) as boolean;
  });
  const isOwner = computed(() => getIsOwner(unref(product), userCollabPermissions));
  const canAccessMoreThanOneProduct = computed(() => productsUserCanAccess.length > 1);
  const cannotAccessAnyProduct = computed(() => productsUserCanAccess.length === 0);
  const locationPermissionFormat = ref<string>('');
  watchEffect(async () => {
    const locationValue = unref(location) ?? null;
    const productValue = unref(product) ?? null;

    if (!locationValue || !productValue) {
      locationPermissionFormat.value = '';
      return;
    }

    locationPermissionFormat.value = await getLocationPermissionFormat(productValue, locationValue);
  });

  return {
    canAccessMoreThanOneProduct,
    cannotAccessAnyProduct,
    isDocumentOwner,
    isOwner,
    locationPermissionFormat,
    productsUserCanAccess,
    routesUserCanAccess,
  };
}
